import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { api } from '@consts/url.const';
import { VehicleGroupsModel } from '@models/vehicle-groups.model';
import { VehicleModels } from '@models/vehicle.model';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class VehicleGroupsService {
  private http = inject(HttpClient);

  public getVehicleGroups(): Observable<VehicleGroupsModel.VehicleGroupListDocument[]> {
    return this.http.get<VehicleGroupsModel.VehicleGroupListDocument[]>(api.vehicleGroups.crud);
  }

  public getVehicleGroupDetails(groupId: string): Observable<VehicleGroupsModel.VehicleGroupDetailsDocument> {
    return this.http.get<VehicleGroupsModel.API.VehicleGroupDetails>(api.vehicleGroups.crudById(groupId)).pipe(
      map(
        (group): VehicleGroupsModel.VehicleGroupDetailsDocument => ({
          _id: group._id,
          name: group.name,
          vehicleIds: group.vehicleIds,
          createdByOrgId: group.createdByOrgId,
          accessList: group.accessList,
          vehiclesCounter: group.vehiclesCounter,
          accessListCounter: group.accessListCounter,
          activeOrgAccessRole: group.activeOrgAccessRole,
        }),
      ),
    );
  }

  public getVehicleGroupVehicles(groupId: string): Observable<VehicleModels.Vehicle[]> {
    return this.http
      .get<VehicleGroupsModel.API.GroupVehiclesResponse>(api.vehicleGroups.vehicles(groupId))
      .pipe(map((response) => response.vehicles));
  }

  public createVehicleGroup(name: string): Observable<VehicleGroupsModel.VehicleGroupListDocument> {
    const trimmed = name.trim();
    return this.http.post<VehicleGroupsModel.API.VehicleGroupListItem>(api.vehicleGroups.crud, {
      name: trimmed,
    });
  }

  public deleteVehicleGroup(id: string): Observable<void> {
    return this.http.delete<void>(api.vehicleGroups.crudById(id));
  }

  public updateVehicleGroup(
    id: string,
    name: string,
  ): Observable<VehicleGroupsModel.VehicleGroupListDocument> {
    const trimmed = name.trim();
    return this.http.patch<VehicleGroupsModel.API.VehicleGroupListItem>(api.vehicleGroups.crudById(id), {
      name: trimmed,
    });
  }

  public addVehiclesToGroups(
    payload: VehicleGroupsModel.AddVehiclesToGroupsAPIPayload,
  ): Observable<VehicleGroupsModel.API.AddVehiclesToGroupsResponse> {
    return this.http.post<VehicleGroupsModel.API.AddVehiclesToGroupsResponse>(api.vehicleGroups.addVehicles, payload);
  }

  public removeVehiclesFromGroup(groupId: string, vehicleIds: number[]): Observable<number[]> {
    return this.http
      .patch<VehicleGroupsModel.VehicleGroupDocument>(api.vehicleGroups.vehicles(groupId), { vehicleIds })
      .pipe(map(() => vehicleIds));
  }

  public updateVehicleGroupAccessListItem(
    groupId: string,
    orgId: string,
    accessType: VehicleGroupsModel.ShareAccessRole,
  ): Observable<VehicleGroupsModel.VehicleGroupSharedWithRow> {
    return this.http
      .patch<VehicleGroupsModel.VehicleGroupSharedWithRow>(
        api.vehicleGroups.accessListItem(groupId, orgId),
        { accessType },
      );
  }

  public removeVehicleGroupAccessListItem(groupId: string, orgId: string): Observable<void> {
    return this.http.delete<void>(api.vehicleGroups.accessListItem(groupId, orgId));
  }

  public joinVehicleGroup(code: string): Observable<VehicleGroupsModel.VehicleGroupListDocument> {
    const trimmed = code.trim();
    return this.http.post<VehicleGroupsModel.API.VehicleGroupListItem>(api.vehicleGroups.join, { inviteCode: trimmed });
  }

  public loadVehicleGroupInviteCode(
    groupId: string,
    accessType: VehicleGroupsModel.ShareAccessRole,
  ): Observable<string> {
    return this.http
      .get<VehicleGroupsModel.API.InviteCodeResponse>(api.vehicleGroups.inviteCode(groupId, accessType))
      .pipe(map((response) => response.inviteCode));
  }

  public loadVehicleGroupAccessList(groupId: string): Observable<VehicleGroupsModel.API.AccessListResponse> {
    return this.http.get<VehicleGroupsModel.API.AccessListResponse>(api.vehicleGroups.accessList(groupId));
  }
}
